<template>
  <div class="price-chart">
    <svg v-if="series.points.length" :viewBox="`0 0 ${width} ${height}`" :width="width" :height="height" class="price-chart__svg">
      <line :x1="pad.left" :x2="width - pad.right" :y1="y(series.max)" :y2="y(series.max)" class="price-chart__grid" />
      <line :x1="pad.left" :x2="width - pad.right" :y1="y(series.min)" :y2="y(series.min)" class="price-chart__grid" />
      <path :d="areaPath" class="price-chart__area" />
      <path :d="linePath" class="price-chart__line" />
      <circle v-for="(p, i) in series.changes" :key="i" :cx="x(p.t)" :cy="y(p.price)" r="2.5" class="price-chart__dot">
        <title>{{ formatDate(p.t) }}: {{ eur(p.price) }}</title>
      </circle>
      <text :x="pad.left - 4" :y="y(series.max) + 3" class="price-chart__label" text-anchor="end">{{ eur(series.max) }}</text>
      <text v-if="series.max !== series.min" :x="pad.left - 4" :y="y(series.min) + 3" class="price-chart__label" text-anchor="end">{{ eur(series.min) }}</text>
      <text :x="pad.left" :y="height - 4" class="price-chart__label">{{ formatDate(series.start) }}</text>
      <text :x="width - pad.right" :y="height - 4" class="price-chart__label" text-anchor="end">{{ formatDate(series.end) }}</text>
    </svg>
    <div v-else class="price-chart__empty">{{ $t("priceHistory.chartEmpty") }}</div>
  </div>
</template>

<script>
const DAY = 24 * 60 * 60 * 1000;

/**
 * Preisverlauf als Stufenlinie je Tag: der Preis gilt ab seiner Erfassung bis zur naechsten Aenderung, bis heute.
 * entries wie von der Preishistorie geliefert (neueste zuerst); months begrenzt den Zeitraum (leer = ab Aufzeichnung).
 */
export default {
  name: "PriceChart",
  props: {
    entries: { type: Array, default: () => [] },
    months: { type: Number, default: null },
    width: { type: Number, default: 360 },
    height: { type: Number, default: 140 },
  },
  data() {
    return { pad: { left: 58, right: 8, top: 8, bottom: 18 } };
  },
  computed: {
    series() {
      const sorted = this.entries
        .filter((e) => e && e.price != null && e.recordedOn)
        .map((e) => ({ t: new Date(e.recordedOn).getTime(), price: Number(e.price) }))
        .sort((a, b) => a.t - b.t);
      if (!sorted.length) return { points: [], changes: [] };

      const end = Date.now();
      let start = sorted[0].t;
      if (this.months) start = Math.max(start, end - this.months * 30.4375 * DAY);

      // Preis zu Beginn des Fensters = letzter Eintrag davor; danach je Aenderung eine Stufe
      const before = sorted.filter((p) => p.t <= start);
      const inside = sorted.filter((p) => p.t > start);
      const startPrice = before.length ? before[before.length - 1].price : inside[0].price;
      const points = [{ t: start, price: startPrice }];
      inside.forEach((p) => {
        points.push({ t: p.t, price: points[points.length - 1].price });
        points.push({ t: p.t, price: p.price });
      });
      points.push({ t: end, price: points[points.length - 1].price });

      const prices = points.map((p) => p.price);
      return { points, changes: inside, start, end, min: Math.min(...prices), max: Math.max(...prices) };
    },
    linePath() {
      return this.series.points.map((p, i) => `${i ? "L" : "M"}${this.x(p.t).toFixed(1)},${this.y(p.price).toFixed(1)}`).join(" ");
    },
    areaPath() {
      const pts = this.series.points;
      const base = (this.height - this.pad.bottom).toFixed(1);
      return `${this.linePath} L${this.x(pts[pts.length - 1].t).toFixed(1)},${base} L${this.x(pts[0].t).toFixed(1)},${base} Z`;
    },
  },
  methods: {
    x(t) {
      const { start, end } = this.series;
      const w = this.width - this.pad.left - this.pad.right;
      return this.pad.left + (end > start ? ((t - start) / (end - start)) * w : w / 2);
    },
    y(price) {
      const { min, max } = this.series;
      const h = this.height - this.pad.top - this.pad.bottom;
      // gleichbleibender Preis: Linie mittig statt am Rand
      return this.pad.top + (max > min ? ((max - price) / (max - min)) * h : h / 2);
    },
    eur(value) {
      return this.$options.filters.formatUnit(value, "€", 2, 2);
    },
    formatDate(t) {
      return new Date(t).toLocaleDateString(this.$i18n.locale);
    },
  },
};
</script>

<style scoped>
.price-chart__svg {
  display: block;
  max-width: 100%;
  height: auto;
}
.price-chart__grid {
  stroke: #e5e5e5;
  stroke-width: 1;
}
.price-chart__area {
  fill: rgba(127, 145, 90, 0.12);
}
.price-chart__line {
  fill: none;
  stroke: #7f915a;
  stroke-width: 1.8;
}
.price-chart__dot {
  fill: #7f915a;
}
.price-chart__label {
  font-size: 10px;
  fill: #777;
}
.price-chart__empty {
  font-size: 12px;
  color: #777;
}
</style>
